<template>
  <div class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray bg-cover bg-center relative overflow-hidden">
    <HyperspeedBackground class="absolute inset-0 z-0" :beam-count="120" color-scheme="rainbow" :speed-factor="1.5" />
    <div class="max-w-xl w-full space-y-8 bg-white/80 backdrop-blur-xl p-8 rounded-2xl overflow-hidden shadow-2xl shadow-cosmicPurple/10 relative z-10">
      <ShineBorder :border-width="2" :duration="24" :shine-color="['#A07CFE', '#FE8FB5', '#A07CFE']" />
      <div class="relative flex items-center justify-center">
        <div class="absolute -top-[25rem] z-10 bg-[#4d7aff] w-[30rem] h-[30rem] rounded-full blur-[100px] opacity-30 appear"></div>
        <LogoMainLink class="z-20" />
      </div>

      <template v-if="!magicLinkSent">
        <div class="text-center space-y-2">
          <h1 class="text-2xl text-gray-900 font-light">Create your account</h1>
          <p class="text-sm text-gray-600">We will email you a sign-in link. No password required.</p>
        </div>

        <div class="space-y-4 max-w-[20rem] mx-auto">
          <div class="space-y-2">
            <label for="register-email" class="block text-sm text-gray-700">Email</label>
            <Input
              id="register-email"
              v-model="email"
              type="email"
              autocomplete="email"
              placeholder="name@example.com"
              class="border-[1px] border-gray-300 rounded-md p-2 outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-900"
            />
          </div>

          <Button
            type="button"
            :disabled="isSending || !email.trim()"
            @click="submitMagicLink"
            class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200 disabled:opacity-50"
          >
            {{ isSending ? 'Sending link...' : 'Email me a sign-in link' }}
          </Button>

          <p v-if="errorMessage" class="text-sm text-red-600">{{ errorMessage }}</p>
        </div>

        <div class="relative my-4">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-300"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        <div class="max-w-[20rem] mx-auto">
          <SocialLoginButtons />
        </div>
      </template>

      <template v-else>
        <div class="text-center space-y-3">
          <h2 class="text-xl font-semibold text-gray-900">Check your inbox</h2>
          <p class="text-sm text-gray-600 leading-relaxed">
            We sent a sign-in link to <span class="font-medium text-gray-900">{{ sentEmail }}</span>.
          </p>
          <button type="button" class="text-sm text-indigo-600 hover:text-indigo-500" @click="magicLinkSent = false">
            Use a different email
          </button>
        </div>
      </template>

      <div class="text-center text-sm text-gray-600 mt-4">
        Already have an account?
        <router-link to="/login" class="font-medium text-indigo-600 hover:text-indigo-500">Sign in</router-link>
      </div>
      <TermsAndConditionsLinks />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import TermsAndConditionsLinks from '@/components/common/TermsAndConditionsLinks.vue'
import LogoMainLink from '@/components/common/LogoMainLink.vue'
import SocialLoginButtons from '@/components/auth/SocialLoginButtons.vue'
import HyperspeedBackground from '@/components/layout/HyperspeedBackground.vue'
import { requestMagicLink } from '@/services/magicLinkService'

const route = useRoute()

const email = ref(typeof route.query.email === 'string' ? route.query.email : '')
const sentEmail = ref('')
const magicLinkSent = ref(false)
const isSending = ref(false)
const errorMessage = ref<string | null>(null)

const submitMagicLink = async () => {
  if (!email.value.trim()) return

  isSending.value = true
  errorMessage.value = null

  try {
    await requestMagicLink(email.value, `${window.location.origin}/app/magic-login`, 'signup')
    sentEmail.value = email.value.trim()
    magicLinkSent.value = true
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { error?: string; detail?: string | string[] } } }
    const raw = ax.response?.data?.error ?? ax.response?.data?.detail
    const msg = Array.isArray(raw) ? raw[0] : raw
    errorMessage.value = typeof msg === 'string' ? msg : 'Could not send a sign-in link. Please try again.'
  } finally {
    isSending.value = false
  }
}
</script>

<style scoped>
.appear {
  animation: appear 1.5s ease-in-out;
}

@keyframes appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
}
</style>
